import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import logo from "@/assets/dr-vahe-logo.png";
import ClinicNAP from "./ClinicNAP";
import { useLiveClinic } from "@/lib/clinicLive";

const currentYear = new Date().getFullYear();

const accreditations = [
  {
    initials: "GMC",
    name: "General Medical Council",
    detail: "Registered medical practitioner",
    href: "https://www.gmc-uk.org",
  },
  {
    initials: "BMA",
    name: "British Medical Association",
    detail: "Member",
    href: "https://www.bma.org.uk",
  },
  {
    initials: "JCCP",
    name: "Joint Council for Cosmetic Practitioners",
    detail: "Registered practitioner",
    href: "https://www.jccp.org.uk",
  },
  {
    initials: "IFAAS",
    name: "International Fellowship in Advanced Aesthetic Science",
    detail: "Fellowship trained",
    href: "https://www.ifaas.com",
  },
  {
    initials: "SF",
    name: "Save Face",
    detail: "Accredited practitioner register",
    href: "https://www.saveface.co.uk",
  },
  {
    initials: "CQC",
    name: "Care Quality Commission",
    detail: "Registered provider",
    href: "https://www.cqc.org.uk",
  },
];

const Footer = () => {
  const clinic = useLiveClinic();
  return (
    <footer className="bg-primary text-primary-foreground py-16 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src={logo} alt={clinic.name} className="h-12 w-12 object-contain bg-background rounded p-1" />
              <span className="font-display text-xl tracking-[0.25em] uppercase">
                {clinic.shortName}
              </span>
            </div>
            <p className="text-sm font-light opacity-70 leading-relaxed">{clinic.tagline}</p>
          </div>
          <ClinicNAP variant="full" className="text-primary-foreground" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] mb-4 opacity-60">Connect</p>
            {clinic.social.website && (
              <a
                href={clinic.social.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block font-light opacity-90 hover:opacity-100 hover:text-accent transition-colors"
              >
                {clinic.social.websiteDisplay}
              </a>
            )}
            {clinic.social.instagram && (
              <a
                href={clinic.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="block font-light opacity-90 hover:opacity-100 hover:text-accent transition-colors"
              >
                {clinic.social.instagramHandle}
              </a>
            )}
          </div>
        </div>

        <div className="mb-12">
          <p className="text-[10px] uppercase tracking-[0.35em] mb-5 opacity-60 flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Credentials &amp; Registrations
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {accreditations.map((a) => (
              <a
                key={a.initials}
                href={a.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 border border-primary-foreground/15 rounded-md px-4 py-3 hover:border-accent hover:bg-primary-foreground/5 transition-colors"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent/60 font-display text-[11px] tracking-[0.15em] text-accent">
                  {a.initials}
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-light leading-tight group-hover:text-accent transition-colors">
                    {a.name}
                  </span>
                  <span className="block text-[10px] font-light opacity-60 leading-tight mt-0.5 truncate">
                    {a.detail}
                  </span>
                </span>
              </a>
            ))}
          </div>
        </div>

        <div className="pt-8 border-t border-primary-foreground/15 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[10px] uppercase tracking-[0.3em] opacity-50">
            © {currentYear} {clinic.name} · All rights reserved
          </p>
          <div className="flex gap-8 text-[10px] uppercase tracking-[0.3em] opacity-50">
            <Link to="/privacy" className="hover:opacity-100 transition-opacity">Privacy</Link>
            <Link to="/terms" className="hover:opacity-100 transition-opacity">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
